import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import api from "./api/axios";
import "./index.css";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Workspace from "./pages/Workspace";
import WorkspaceCom from "./pages/WorkspaceCom";
import WorkspaceCom2 from "./pages/WorkspaceCom2";
import ScreeningAgent from "./pages/ScreeningAgent";
import MyAgents from "./pages/MyAgents";
import ChatInterface from "./pages/ChatInterface";
import SharingAgent from "./pages/SharingAgent";
import Logs from "./pages/Logs";
import AgentCreation from "./pages/AgentCreation";
import Artifacts from "./pages/Artifacts";
import Analytics from "./pages/Analytics";
import Dashboard from "./pages/Dashboard";
import Settings from "./pages/Settings/Settings";
import ProfileManagement from "./pages/Settings/ProfileManagement";
import UserRole from "./pages/Settings/UserRole";
import Notification from "./pages/Settings/Notification";
import Subscription from "./pages/Settings/Subscription";
import WorkspaceManagement from "./pages/Settings/WorkSpaceManagement";
import Layout from "./Layout";
import Kb from "./pages/Kb";
import OAuthCallback from "./pages/OAuthCallback";
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import Generator from "./pages/Generator";
import ForgotPassword from "./pages/ForgotPassword";
import Plans from "./pages/Plans";
import GoogleDrivePage from "./pages/Knowladgebase/Gdrive";
import { ThemeProvider } from "./ThemeContext";
import ZapierIntegrationPage from "./pages/Knowladgebase/ZapierIntegrationPage";
import AttachmentMethodsPage from "./pages/Knowladgebase/attachmentMethods";
import UrlImportPage from "./pages/Knowladgebase/UrlImportPage";
import CrawlPage from "./pages/Knowladgebase/Crawlpage";
import KbAdd from "./pages/KbAdd";
import AgentChatPage from "./pages/Knowladgebase/Agentchat";
import AdminPanel from "./pages/AdminPanel";
import AdminPanelForUsers from "./pages/AdminPanelForUsers";
import AdminUserDetailPage from "./pages/AdminUserDetailPage";
import AdminLogin from "./pages/AdminLogin"; 
import HomeNew from "./pages/NewUI/Homenew";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
/* ---------------------------------------------------------
    ✅ THE BOUNCER: PageGuard Component
--------------------------------------------------------- */
const PageGuard = ({ children, pageLabel, permissions, loading }) => {
  if (loading) return <div className="vh-100" style={{backgroundColor: '#00008B'}} />;

  const isInternal = localStorage.getItem("isInternalAdmin") === "true";

  if (pageLabel === "KinexarAdmin") {
    if (!isInternal) {
      return <Navigate to="/home" replace />;
    }
    return children;
  }

  if (isInternal) {
    return <Navigate to="/sa-ctrl-x9k2" replace />;
  }

  if (permissions?.isAdmin) return children;

  if (pageLabel === "Home" || pageLabel === "Profile Management" || pageLabel === "Settings") {
      return children;
  }

  if (permissions?.noRoleDefined) return children;

  const allowedRead = (permissions?.read_access || "").split(",").map(s => s.trim());
  const allowedWrite = (permissions?.write_access || "").split(",").map(s => s.trim());
  const allowedBoth = (permissions?.both_access || "").split(",").map(s => s.trim());

  const hasAccess =
    allowedRead.includes(pageLabel) ||
    allowedWrite.includes(pageLabel) ||
    allowedBoth.includes(pageLabel);

  if (!hasAccess) return <Navigate to="/new-home" replace />;

  return children;
};

function App() {
  const location = useLocation();
  const navigate = useNavigate();
const currentPath = location.pathname
  .toLowerCase()
  .replace(/\/+$/, "");
  const [permissions, setPermissions] = useState(null);
  const [loadingPerms, setLoadingPerms] = useState(true);

  const ADMIN_AUTH_PATH = "/sa-auth-x92";

  useEffect(() => {
    const fetchPerms = async () => {
      const token = localStorage.getItem("token");
      const isInternal = localStorage.getItem("isInternalAdmin") === "true";

      if (!token) {
        setPermissions(null); 
        setLoadingPerms(false);
        return;
      }

      if (isInternal) {
        setPermissions({ isInternal: true }); 
        setLoadingPerms(false);
        return;
      }

      try {
        const res = await api.get(`/user-roles/me?t=${new Date().getTime()}`);
        setPermissions(res.data);

        const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
        
        if (res.data.isAdmin || res.data.has_workspace || res.data.is_invited) {
          if (storedUser.hasWorkspace !== true) {
            storedUser.hasWorkspace = true;
            localStorage.setItem("user", JSON.stringify(storedUser));
          }
        }
      } catch (err) {
        console.error("Failed to fetch permissions");
        setPermissions(null);
      } finally {
        setLoadingPerms(false);
      }
    };
    fetchPerms();
  }, [location.pathname]);

  const renderWithLayout = (component) => {
    const token = localStorage.getItem("token");
    const isInternal = localStorage.getItem("isInternalAdmin") === "true";
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    const publicPaths = [
      "/login",
      "/signup",
      ADMIN_AUTH_PATH,
      "/workspace",
      "/workspace-company",
      "/workspace-invite",
      "/oauth/callback",
      "/oauth-success",
      "/forgot-password",
      "/share-chat"
    ];
    
    const isPublicRoute = publicPaths.some(p => currentPath.startsWith(p));

    if (!token && !isPublicRoute) {
      return <Navigate to="/login" replace />;
    }

    if (token && !isInternal && user?.hasWorkspace === false && !isPublicRoute) {
      return <Navigate to="/workspace" replace />;
    }

    if (token && (currentPath === "/login" || currentPath === "/signup" || currentPath === "/")) {
      if (!isInternal && user?.hasWorkspace === false) {
        return <Navigate to="/workspace" replace />;
      }
      return <Navigate to={isInternal ? "/sa-ctrl-x9k2" : "/new-home"} replace />;
    }

    if (token && isInternal) {
      const adminRoutes = [
        "/sa-ctrl-x9k2",
        "/sa-users-u8v1",
        "/sa-user-x9k2/"
      ];
      if (!adminRoutes.some(route => currentPath.startsWith(route))) {
        return <Navigate to="/sa-ctrl-x9k2" replace />;
      }
    }

    // ✅ ALL routes that should bypass Layout completely (no top navbar, no title strip)
    const noLayoutRoutes = [
      ADMIN_AUTH_PATH,
      "/sa-ctrl-x9k2",
      "/sa-users-u8v1",
      "/sa-user-x9k2/",
      "/new-home",
      "/chatinterface",
      "/share-chat",
      "/dashboard",
      "/analytics",
      "/home",
      "/create-agent",
      "/screeningagent",
      "/kb",
      "/logs",
      "/settings"
    ];

const skipLayout = noLayoutRoutes.some(
  route =>
    currentPath === route ||
    currentPath.startsWith(route + "/")
);
    if (skipLayout) return component;
    return <Layout>{component}</Layout>;
  };

  return (
    <>
       {/* ✅ MOVED COMPLETELY OUTSIDE THEMEPROVIDER */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        style={{ zIndex: 2147483647 }} 
      />
     
      <ThemeProvider>
      
        {renderWithLayout(
          <Routes>
            {/* --- PUBLIC ROUTES --- */}
            <Route path="/" element={<Navigate to="/login" />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/oauth-success" element={<OAuthCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword/>} />
            <Route path="/workspace" element={<Workspace />} />
            <Route path="/workspace-company" element={<WorkspaceCom />} />
            <Route path="/workspace-invite" element={<WorkspaceCom2 />} />
            <Route path="/share-chat/:shareToken" element={<ChatInterface />} />
            <Route path={ADMIN_AUTH_PATH} element={<AdminLogin />} />

            {/* --- PROTECTED ROUTES --- */}
            <Route path="/home" element={
              <PageGuard pageLabel="Home" permissions={permissions} loading={loadingPerms}>
                <Home />
              </PageGuard>
            } />
            
            <Route path="/dashboard" element={
              <PageGuard pageLabel="Dashboard" permissions={permissions} loading={loadingPerms}>
                <Dashboard />
              </PageGuard>
            } />
            
            <Route path="/kb" element={
              <PageGuard pageLabel="Kb" permissions={permissions} loading={loadingPerms}>
                <Kb />
              </PageGuard>
            } />

            <Route path="/artifacts" element={
              <PageGuard pageLabel="Artifacts" permissions={permissions} loading={loadingPerms}>
                <Artifacts />
              </PageGuard>
            } />

            <Route path="/analytics" element={
              <PageGuard pageLabel="Analytics" permissions={permissions} loading={loadingPerms}>
                <Analytics />
              </PageGuard>
            } />

            <Route path="/logs" element={
              <PageGuard pageLabel="Logs" permissions={permissions} loading={loadingPerms}>
                <Logs />
              </PageGuard>
            } />

            <Route path="/profilemanagement" element={
              <PageGuard pageLabel="Profile Management" permissions={permissions} loading={loadingPerms}>
                <ProfileManagement /> 
              </PageGuard>
            } />

            <Route path="/userrole" element={
              <PageGuard pageLabel="User Role" permissions={permissions} loading={loadingPerms}>
                <UserRole />
              </PageGuard>
            } />

            <Route path="/notification" element={
              <PageGuard pageLabel="Notifications" permissions={permissions} loading={loadingPerms}>
                <Notification />
              </PageGuard>
            } />

            <Route path="/subscription" element={
              <PageGuard pageLabel="Subscription" permissions={permissions} loading={loadingPerms}>
                <Subscription />
              </PageGuard>
            } />

            <Route path="/workspacemanagement" element={
              <PageGuard pageLabel="Workspace Management" permissions={permissions} loading={loadingPerms}>
                <WorkspaceManagement />
              </PageGuard>
            } />

            <Route path="/settings" element={
              <PageGuard pageLabel="Settings" permissions={permissions} loading={loadingPerms}>
                <Settings />
              </PageGuard>
            } />

            {/* ✅ NEW HOME - completely standalone, no Layout wrapper */}
            <Route path="/new-home" element={<HomeNew />} />

            {/* ADMIN ROUTES */}
            <Route path="/sa-ctrl-x9k2" element={
              <PageGuard pageLabel="KinexarAdmin" permissions={permissions} loading={loadingPerms}>
                <AdminPanel />
              </PageGuard>
            } />

            <Route path="/sa-users-u8v1" element={
              <PageGuard pageLabel="KinexarAdmin" permissions={permissions} loading={loadingPerms}>
                <AdminPanelForUsers />
              </PageGuard>
            } />

            <Route path="/sa-user-x9k2/:id" element={
              <PageGuard pageLabel="KinexarAdmin" permissions={permissions} loading={loadingPerms}>
                <AdminUserDetailPage />
              </PageGuard>
            } />

            {/* Client Specific Pages */}
            <Route path="/create-agent" element={<AgentCreation />} />
            <Route path="/chat-bot" element={<Generator />} />
            <Route path="/plans" element={<Plans/>} />
            <Route path="/screeningagent" element={<ScreeningAgent />} />
            <Route path="/myagents" element={<MyAgents />} />
            <Route path="/chatinterface" element={<ChatInterface />} />
            <Route path="/chatinterface/:agentId" element={<ChatInterface />} />
            <Route path="/sharingagent" element={<SharingAgent />} />
            <Route path="/drive-connect" element={<GoogleDrivePage />} />
            <Route path="/zapier-integration" element={<ZapierIntegrationPage />} />
            <Route path="/attachement" element={<AttachmentMethodsPage />} />
            <Route path="/url-import" element={<UrlImportPage />} />
            <Route path="/crawl" element={<CrawlPage />} />
            <Route path="/agent-chat" element={<AgentChatPage />} />
            <Route path="/kbadd" element={<KbAdd/>} />

            <Route
              path="*"
              element={
                (() => {
                  const token = localStorage.getItem("token");
                  const isInternal = localStorage.getItem("isInternalAdmin") === "true";
                  const user = JSON.parse(localStorage.getItem("user") || "{}");

                  if (!token) return <Navigate to="/login" replace />;
                  if (isInternal) return <Navigate to="/sa-ctrl-x9k2" replace />;
                  if (user?.hasWorkspace === false) return <Navigate to="/workspace" replace />;

                  return <Navigate to="/new-home" replace />;
                })()
              }
            />        
          </Routes>
        )}
      </ThemeProvider>
    </>
  );
}

export default App;